import { PaddingOptions, Popup as MapboxPopup } from 'mapbox-gl';
import { ComponentProps, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Popup } from 'react-map-gl/mapbox';
import useMap from '../../hooks/useMap';
import PopupOffset from '../../types/PopupOffset';

type Anchor = 'center' | 'top' | 'bottom' | 'left' | 'right' | 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right';

const bottomPreferredAnchors: Anchor[] = ['bottom', 'top', 'bottom-left', 'bottom-right', 'top-left', 'top-right', 'left', 'right', 'center'];
const topPreferredAnchors: Anchor[] = ['top', 'bottom', 'top-left', 'top-right', 'bottom-left', 'bottom-right', 'left', 'right', 'center'];

const anchorValidators: Record<
  Anchor,
  (
    mapHeight: number,
    mapWidth: number,
    mapPadding: PaddingOptions,
    popupHeight: number,
    popupWidth: number,
    x: number,
    y: number,
    offsets: [number, number]
  ) => boolean
> = {
  bottom: (mapHeight, mapWidth, mapPadding, popupHeight, popupWidth, x, y, [horizontalOffset, verticalOffset]) => {
    // Not enough room above
    if (y - popupHeight + verticalOffset < (mapPadding?.top ?? 0)) {
      return false;
    }
    // Not enough room to the left
    if (x - popupWidth / 2 + horizontalOffset < (mapPadding?.left ?? 0)) {
      return false;
    }
    // Not enough room to the right
    if (x + popupWidth / 2 + horizontalOffset > mapWidth - (mapPadding?.right ?? 0)) {
      return false;
    }
    return true;
  },
  'bottom-left': (mapHeight, mapWidth, mapPadding, popupHeight, popupWidth, x, y, [horizontalOffset, verticalOffset]) => {
    // Not enough room above
    if (y - popupHeight + verticalOffset < (mapPadding?.top ?? 0)) {
      return false;
    }
    // Not enough room to the left
    if (x + horizontalOffset < (mapPadding?.left ?? 0)) {
      return false;
    }
    // Not enough room to the right
    if (x + popupWidth + horizontalOffset > mapWidth - (mapPadding?.right ?? 0)) {
      return false;
    }
    return true;
  },
  'bottom-right': (mapHeight, mapWidth, mapPadding, popupHeight, popupWidth, x, y, [horizontalOffset, verticalOffset]) => {
    // Not enough room above
    if (y - popupHeight + verticalOffset < (mapPadding?.top ?? 0)) {
      return false;
    }
    // Not enough room to the left
    if (x - popupWidth + horizontalOffset < (mapPadding?.left ?? 0)) {
      return false;
    }
    // Not enough room to the right
    if (x + horizontalOffset > mapWidth - (mapPadding?.right ?? 0)) {
      return false;
    }
    return true;
  },
  center: (mapHeight, mapWidth, mapPadding, popupHeight, popupWidth, x, y, [horizontalOffset, verticalOffset]) => {
    // Not enough room above
    if (y - popupHeight / 2 + verticalOffset < (mapPadding?.top ?? 0)) {
      return false;
    }
    // Not enough room below
    if (y + popupHeight / 2 + verticalOffset > mapHeight - (mapPadding?.bottom ?? 0)) {
      return false;
    }
    // Not enough room to the left
    if (x - popupWidth / 2 + horizontalOffset < (mapPadding?.left ?? 0)) {
      return false;
    }
    // Not enough room to the right
    if (x + popupWidth / 2 + horizontalOffset > mapWidth - (mapPadding?.right ?? 0)) {
      return false;
    }
    return true;
  },
  left: (mapHeight, mapWidth, mapPadding, popupHeight, popupWidth, x, y, [horizontalOffset, verticalOffset]) => {
    // Not enough room above
    if (y - popupHeight / 2 + verticalOffset < (mapPadding?.top ?? 0)) {
      return false;
    }
    // Not enough room below
    if (y + popupHeight / 2 + verticalOffset > mapHeight - (mapPadding?.bottom ?? 0)) {
      return false;
    }
    // Not enough room to the right
    if (x + popupWidth + horizontalOffset > mapWidth - (mapPadding?.right ?? 0)) {
      return false;
    }
    return true;
  },
  right: (mapHeight, mapWidth, mapPadding, popupHeight, popupWidth, x, y, [horizontalOffset, verticalOffset]) => {
    // Not enough room above
    if (y - popupHeight / 2 + verticalOffset < (mapPadding?.top ?? 0)) {
      return false;
    }
    // Not enough room below
    if (y + popupHeight / 2 + verticalOffset > mapHeight - (mapPadding?.bottom ?? 0)) {
      return false;
    }
    // Not enough room to the left
    if (x - popupWidth + horizontalOffset < (mapPadding?.left ?? 0)) {
      return false;
    }
    return true;
  },
  top: (mapHeight, mapWidth, mapPadding, popupHeight, popupWidth, x, y, [horizontalOffset, verticalOffset]) => {
    // Not enough room below
    if (y + popupHeight + verticalOffset > mapHeight - (mapPadding?.bottom ?? 0)) {
      return false;
    }
    // Not enough room to the left
    if (x - popupWidth / 2 + horizontalOffset < (mapPadding?.left ?? 0)) {
      return false;
    }
    // Not enough room to the right
    if (x + popupWidth / 2 + horizontalOffset > mapWidth - (mapPadding?.right ?? 0)) {
      return false;
    }
    return true;
  },
  'top-left': (mapHeight, mapWidth, mapPadding, popupHeight, popupWidth, x, y, [horizontalOffset, verticalOffset]) => {
    // Not enough room below
    if (y + popupHeight + verticalOffset > mapHeight - (mapPadding?.bottom ?? 0)) {
      return false;
    }
    // Not enough room to the left
    if (x + horizontalOffset < (mapPadding?.left ?? 0)) {
      return false;
    }
    // Not enough room to the right
    if (x + popupWidth + horizontalOffset > mapWidth - (mapPadding?.right ?? 0)) {
      return false;
    }
    return true;
  },
  'top-right': (mapHeight, mapWidth, mapPadding, popupHeight, popupWidth, x, y, [horizontalOffset, verticalOffset]) => {
    // Not enough room below
    if (y + popupHeight + verticalOffset > mapHeight - (mapPadding?.bottom ?? 0)) {
      return false;
    }
    // Not enough room to the left
    if (x - popupWidth + horizontalOffset < (mapPadding?.left ?? 0)) {
      return false;
    }
    // Not enough room to the right
    if (x + horizontalOffset > mapWidth - (mapPadding?.right ?? 0)) {
      return false;
    }
    return true;
  }
};

// Remove anchor since mapbox only auto-positions the popup when anchor isn't specified, so we will do the same.
// Override the offset type to enforce a type that we can use to better determine the proper anchor.
type Props = Omit<ComponentProps<typeof Popup>, 'anchor' | 'offset'> & {
  offset: PopupOffset;
  preferTopAnchor?: boolean;
  /**
   * Close the popup when the user clicks elsewhere on the map.
   *
   * This is an alternative to mapbox's `closeOnClick`, which closes the popup on `preclick` - before any `click`
   * handler runs. Closing on `click` instead means handlers that skip their default behavior while the popup is open
   * (e.g. the custom route context menu) still see the popup as open when they handle the dismissing click.
   *
   * Requires `onClose` to clear whatever state renders the popup.
   */
  closeOnMapClick?: boolean;
};

/**
 * A wrapper around the Popup component from react-map-gl that accounts for map padding
 * when auto-positioning the popup in respect to the anchor.
 * https://github.com/mapbox/mapbox-gl-js/issues/11841
 * @param props [Popup](https://docs.mapbox.com/mapbox-gl-js/api/markers/#popup) props
 * @returns Popup
 */
const PaddingAwarePopup = ({ closeOnMapClick = false, preferTopAnchor = false, ...props }: Props) => {
  const map = useMap();
  const padding = map?.getPadding();

  const popupRef = useRef<MapboxPopup | undefined>(undefined);
  const [paddingAwareAnchor, setPaddingAwareAnchor] = useState<Anchor>();

  const anchors = useMemo(() => (preferTopAnchor ? topPreferredAnchors : bottomPreferredAnchors), [preferTopAnchor]);

  // When the popup opens, it will auto-position and default to "bottom" (the popup renders above the lat/lng).
  // We can use the size of the popup along with a number of other factors to determine if the map padding should
  // have impacted the auto-positioning of the popup. If it should have, we save what the correct anchor should be
  // and then pass that to the popup so that it no longer tries to auto-position.
  const calculateAnchor = useCallback(
    (popup: MapboxPopup) => {
      if (!map) {
        return;
      }
      if (!padding) {
        // Do nothing, let mapbox do its thing
        return;
      }
      const { x, y } = map.project(popup.getLngLat());
      const { height: popupHeight, width: popupWidth } = popup?.getElement()?.getBoundingClientRect() ?? { height: 0, width: 0 };
      const { height: mapHeight, width: mapWidth } = map.getContainer().getBoundingClientRect();
      const validAnchor = anchors.find(anchor => {
        return anchorValidators[anchor](mapHeight, mapWidth, padding, popupHeight, popupWidth, x, y, props.offset[anchor] ?? [0, 0]);
      });
      if (validAnchor) {
        setPaddingAwareAnchor(validAnchor);
      } else {
        setPaddingAwareAnchor(undefined);
      }
    },
    [anchors, map, padding, props.offset]
  );

  useEffect(() => {
    const onMoveEnd = () => {
      if (popupRef.current) {
        calculateAnchor(popupRef.current);
      }
    };

    map?.on('moveend', onMoveEnd);
    return () => {
      map?.off('moveend', onMoveEnd);
    };
  }, [calculateAnchor, map]);

  useEffect(() => {
    if (!map || !closeOnMapClick) {
      return undefined;
    }

    // Clicks within the popup are not map events since the popup is rendered outside of the map's canvas container,
    // so any click we see here is a click elsewhere on the map. Removing the popup fires its close event, which
    // react-map-gl forwards to onClose.
    const onMapClick = () => {
      popupRef.current?.remove();
    };

    map.on('click', onMapClick);
    return () => {
      map.off('click', onMapClick);
    };
  }, [closeOnMapClick, map]);

  return (
    <Popup
      {...props}
      anchor={paddingAwareAnchor}
      offset={paddingAwareAnchor ? props.offset[paddingAwareAnchor] : undefined}
      onOpen={e => {
        popupRef.current = e.target;
        calculateAnchor(e.target);
        props.onOpen?.(e);
      }}
    />
  );
};

export default PaddingAwarePopup;
